/**
 * BB12 item 3 (2026-09-10, ADR-032) — redação de PII antes de qualquer coisa ir ao Sentry.
 *
 * Achado M2 do `code-review-2026-09-10.md`: quando a `msg` de erro do back não estava no
 * mapa PT, ela ia inteira (até 500 caracteres) para o `extra` do Sentry. Mensagem de
 * servidor pode carregar nº de processo, nome de parte ou valor — e a política do épico é
 * Sentry sem dado pessoal (ADR-014; o `ResultadoNegativo` foi desenhado justamente para
 * "nunca valores ou nome do cliente").
 *
 * Duas regras, aplicadas nesta ordem:
 *  1. **dígitos** — toda sequência de 6 ou mais dígitos vira `[redigido]`. Pega nº de
 *     processo sem pontuação (20 dígitos), CPF/CNPJ, id de registro. Num CNJ **formatado**
 *     (`0001234-56.2026.8.26.0100`) sai o número sequencial, que é a parte discriminante, e
 *     os grupos curtos ficam — baixar o limite para 4 dígitos passaria a redigir ano e
 *     código HTTP. Não pega ano, valor curto, código HTTP nem `1 de 12`.
 *  2. **tamanho** — o que sobra é cortado em 120 caracteres, com `…` no fim quando cortou.
 *     Uma mensagem longa de servidor costuma trazer trecho de query ou payload; 120
 *     caracteres bastam para agrupar o erro no Sentry.
 *
 * `redigirExtra` aplica isso a todo valor de texto do `extra`, **exceto** as chaves
 * técnicas (`CHAVES_TECNICAS`): `report_id` é um uuid v4 cujos segmentos podem ser 8 ou 12
 * dígitos e é justamente o que liga o evento ao cálculo; `codigo`/`motivo`/`status` são
 * enums nossos. Número e booleano passam intactos.
 *
 * **Limite conhecido:** só o `extra` é redigido. Um `Error` entregue a
 * `Sentry.captureException` viaja com a própria `message` — hoje nenhum caminho manda a
 * `msg` do back dentro de um `Error` para o `capturarErro` (o ramo `success:false` da saga
 * cria o `Error`, mas ele só chega ao `onError`/`registrarRecusaV2`, que usa `extra`).
 */

/** Limite de caracteres de um texto livre no `extra` do Sentry. */
export const LIMITE_TEXTO = 120;

/** Marcador que substitui a sequência de dígitos. */
export const MARCA_REDIGIDO = '[redigido]';

/** 6+ dígitos seguidos: nº de processo, CPF/CNPJ, id. Ano e valor curto não casam. */
const SEQUENCIA_DIGITOS = /\d{6,}/g;

/**
 * Chaves do `extra` que NÃO são texto livre e precisam chegar íntegras ao Sentry:
 * ids técnicos nossos e enums de motivo/estado.
 */
export const CHAVES_TECNICAS: readonly string[] = [
  'report_id',
  'calculation_id',
  'codigo',
  'motivo',
  'status',
  'versao',
  'versao_payload',
  'versao_regra_calculo',
  'action',
  'tentativas',
];

/** Aplica as 2 regras a um texto livre. Entrada não-string volta como string vazia. */
export const redigirTexto = (valor: unknown): string => {
  if (typeof valor !== 'string') return '';
  const semDigitos = valor.replace(SEQUENCIA_DIGITOS, MARCA_REDIGIDO);
  return semDigitos.length > LIMITE_TEXTO
    ? `${semDigitos.slice(0, LIMITE_TEXTO)}…`
    : semDigitos;
};

/**
 * Redige os textos livres de um `extra` do Sentry. Preserva a forma do objeto: números,
 * booleanos, `null` e as `CHAVES_TECNICAS` passam intactos; array de texto é redigido item
 * a item. Não desce em objeto aninhado (nenhum `extra` nosso tem um).
 */
export const redigirExtra = <T extends Record<string, unknown>>(
  extra: T | undefined,
): T | undefined => {
  if (!extra) return extra;
  const saida: Record<string, unknown> = {};
  for (const [chave, valor] of Object.entries(extra)) {
    if (CHAVES_TECNICAS.includes(chave)) {
      saida[chave] = valor;
    } else if (typeof valor === 'string') {
      saida[chave] = redigirTexto(valor);
    } else if (Array.isArray(valor)) {
      saida[chave] = valor.map((v) => (typeof v === 'string' ? redigirTexto(v) : v));
    } else {
      saida[chave] = valor;
    }
  }
  return saida as T;
};
